import { ApiService } from "./ApiService";
import {
  MfaChallengePayload,
  MfaChallengeResponse,
  MfaSetupPayload,
  MfaSetupResponse,
  MfaVerifySetupPayload,
  MfaVerifySetupResponse,
} from "@/domain/MfaDto";

export interface LoginWithEmailPayload {
  email: string;
  password: string;
  turnstileToken: string;
}

export interface LoginWithEmailResponse {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  challengeName?: "SOFTWARE_TOKEN_MFA" | "MFA_SETUP" | "NEW_PASSWORD_REQUIRED";
  session?: string;
  email?: string;
}

export interface NewPasswordChallengePayload {
  session: string;
  email: string;
  newPassword: string;
}

export interface NewPasswordChallengeResponse {
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  challengeName?: "SOFTWARE_TOKEN_MFA" | "MFA_SETUP";
  session?: string;
  email?: string;
}

export interface SignupPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  turnstileToken: string;
}

export interface SendVerificationCodePayload {
  email: string;
}

export interface SendVerificationCodeResponse {
  success: boolean;
  alreadyVerified?: boolean;
}

export interface ConfirmVerificationCodePayload {
  email: string;
  code: string;
}

export interface ConfirmVerificationCodeResponse {
  success: boolean;
}

export interface EmailVerificationStatusResponse {
  verified: boolean;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  otpCode: string;
  verificationMethod: "totp" | "email";
}

export interface ChangePasswordResponse {
  success: boolean;
}

export interface SendPasswordChangeOtpPayload {
  email: string;
}

export interface SendPasswordChangeOtpResponse {
  success: boolean;
}

export interface LinkGoogleAccountResponse {
  success: boolean;
}

export interface MfaSetupAuthenticatedResponse {
  secretCode: string;
  session: string;
}

export interface VerifyMfaSetupAuthenticatedPayload {
  session: string;
  code: string;
}

export interface VerifyMfaSetupAuthenticatedResponse {
  success: boolean;
  backupCodes?: string[];
}

export class AuthService {
  constructor(private readonly apiService: ApiService) {}
  async loginWithEmail(
    payload: LoginWithEmailPayload
  ): Promise<LoginWithEmailResponse> {
    return this.apiService.request<LoginWithEmailResponse>(
      "/auth/email/login",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async respondToMfaChallenge(
    payload: MfaChallengePayload
  ): Promise<MfaChallengeResponse> {
    return this.apiService.request<MfaChallengeResponse>(
      "/auth/email/mfa/verify",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async respondToNewPasswordChallenge(
    payload: NewPasswordChallengePayload
  ): Promise<NewPasswordChallengeResponse> {
    return this.apiService.request<NewPasswordChallengeResponse>(
      "/auth/email/new-password",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async setupMfa(payload: MfaSetupPayload): Promise<MfaSetupResponse> {
    return this.apiService.request<MfaSetupResponse>("/auth/email/mfa/setup", {
      method: "POST",
      body: JSON.stringify(payload),
      requireAuth: false,
    });
  }

  async verifyMfaSetup(
    payload: MfaVerifySetupPayload
  ): Promise<MfaVerifySetupResponse> {
    return this.apiService.request<MfaVerifySetupResponse>(
      "/auth/email/mfa/confirm",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async sendEmailVerificationCode(
    payload: SendVerificationCodePayload
  ): Promise<SendVerificationCodeResponse> {
    return this.apiService.request<SendVerificationCodeResponse>(
      "/auth/email/send-verification",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async confirmEmailVerificationCode(
    payload: ConfirmVerificationCodePayload
  ): Promise<ConfirmVerificationCodeResponse> {
    return this.apiService.request<ConfirmVerificationCodeResponse>(
      "/auth/email/confirm-verification",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: false,
      }
    );
  }

  async checkEmailVerificationStatus(
    email: string
  ): Promise<EmailVerificationStatusResponse> {
    return this.apiService.request<EmailVerificationStatusResponse>(
      "/auth/email/verification-status",
      {
        method: "GET",
        params: { email },
        requireAuth: false,
      }
    );
  }

  async changePassword(
    payload: ChangePasswordPayload
  ): Promise<ChangePasswordResponse> {
    return this.apiService.request<ChangePasswordResponse>(
      "/auth/change-password",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: true,
      }
    );
  }

  async sendPasswordChangeOtp(
    payload: SendPasswordChangeOtpPayload
  ): Promise<SendPasswordChangeOtpResponse> {
    return this.apiService.request<SendPasswordChangeOtpResponse>(
      "/auth/change-password/send-otp",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: true,
      }
    );
  }

  async linkGoogleAccount(): Promise<LinkGoogleAccountResponse> {
    return this.apiService.request<LinkGoogleAccountResponse>(
      "/auth/link-google",
      {
        method: "POST",
        requireAuth: true,
      }
    );
  }

  async setupMfaAuthenticated(): Promise<MfaSetupAuthenticatedResponse> {
    return this.apiService.request<MfaSetupAuthenticatedResponse>(
      "/auth/settings/mfa/setup",
      {
        method: "POST",
        requireAuth: true,
      }
    );
  }

  async verifyMfaSetupAuthenticated(
    payload: VerifyMfaSetupAuthenticatedPayload
  ): Promise<VerifyMfaSetupAuthenticatedResponse> {
    return this.apiService.request<VerifyMfaSetupAuthenticatedResponse>(
      "/auth/settings/mfa/verify-setup",
      {
        method: "POST",
        body: JSON.stringify(payload),
        requireAuth: true,
      }
    );
  }
}
